import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2, AlertCircle, Check } from 'lucide-react';
import * as api from '../../lib/api';
import { uploadUrl } from '../../lib/uploads';

/** Matches the server's accept list exactly — SVG is refused there, so don't offer it. */
const ACCEPTED = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_BYTES = 2 * 1024 * 1024;



export default function AvatarField({ user, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);

  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  const src = preview ?? uploadUrl(user.avatar_url);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    // Reset immediately so choosing the same file twice still fires a change.
    event.target.value = '';
    if (!file) return;

    // Check size here as well as on the server. The server is the authority,
    // but failing before a two-megabyte round trip is kinder on a phone.
    if (file.size > MAX_BYTES) {
      setStatus({ ok: false, message: 'That image is larger than 2MB. Try a smaller one.' });
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    setBusy(true);
    setStatus(null);

    try {
      await api.uploadAvatar(file);
      setStatus({ ok: true, message: 'Photo updated' });
      onChanged();
    } catch (err) {
      // Drop the optimistic preview — leaving it up would imply a save that
      // did not happen.
      setPreview(null);
      setStatus({ ok: false, message: err.message });
    } finally {
      URL.revokeObjectURL(localPreview);
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setStatus(null);
    try {
      await api.deleteAvatar();
      setPreview(null);
      setStatus({ ok: true, message: 'Photo removed' });
      onChanged();
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-5">
        <div className="relative shrink-0">
          {src ? (
            <img src={src} alt="" aria-hidden="true"
                 className="h-20 w-20 rounded-full object-cover ring-1 ring-slate-200" />
          ) : (
            <span aria-hidden="true"
                  className="grid h-20 w-20 place-items-center rounded-full bg-brand-100 text-xl font-extrabold text-brand-800">
              {initials || '—'}
            </span>
          )}
          {busy && (
            <span className="absolute inset-0 grid place-items-center rounded-full bg-white/70">
              <Loader2 className="h-5 w-5 animate-spin text-brand-600" aria-hidden="true" />
            </span>
          )}
        </div>

        <div className="min-w-0">
          <h2 className="text-sm font-extrabold text-slate-900">Profile photo</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            PNG, JPEG, WebP or GIF, up to 2MB. Optional — your initials are used otherwise.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" disabled={busy} onClick={() => inputRef.current?.click()}
                    className="btn-ghost py-2 text-xs">
              <ImagePlus className="h-3.5 w-3.5" aria-hidden="true" />
              {user.avatar_url ? 'Replace' : 'Upload'}
            </button>
            {user.avatar_url && (
              <button type="button" disabled={busy} onClick={remove} className="btn-ghost py-2 text-xs">
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Remove
              </button>
            )}
            {status && (
              <span role="status" className={`inline-flex items-center gap-1 text-xs font-semibold ${status.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                {status.ok ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />}
                {status.message}
              </span>
            )}
          </div>

          <input ref={inputRef} type="file" accept={ACCEPTED} onChange={pick}
                 className="sr-only" aria-label="Choose a profile photo" />
        </div>
      </div>
    </section>
  );
}
